import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../providers/AuthProvider";
import { RealtimeChannel } from "@supabase/supabase-js";

export type UserStatus = "online" | "offline" | "away" | "busy";

export interface PresenceUser {
  userId: string;
  status: UserStatus;
  lastSeen?: string;
  isAdmin?: boolean;
}

export function useSupportPresence(
  ticketId: string | null,
  isAdmin: boolean = true,
) {
  const [onlineUsers, setOnlineUsers] = useState<Map<string, PresenceUser>>(
    new Map(),
  );
  const [isOnline, setIsOnline] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const channelRef = useRef<RealtimeChannel | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const presenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Enviar status de "digitando"
  const sendTyping = useCallback(() => {
    if (!ticketId || !channelRef.current) return;

    // Limpar timeout anterior
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Enviar evento de typing
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user && channelRef.current) {
        channelRef.current.send({
          type: "broadcast",
          event: "typing",
          payload: {
            userId: user.id,
            timestamp: new Date().toISOString(),
          },
        });

        // Remover typing após 3 segundos de inatividade
        typingTimeoutRef.current = setTimeout(() => {
          setTypingUsers((prev) => {
            const next = new Set(prev);
            next.delete(user.id);
            return next;
          });
        }, 3000);
      }
    });
  }, [ticketId]);

  // Atualizar status de presença
  const updatePresence = useCallback(
    async (status: UserStatus) => {
      if (!ticketId || !channelRef.current) return;

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      await channelRef.current.track({
        userId: user.id,
        status,
        isAdmin,
        lastSeen: new Date().toISOString(),
      });
    },
    [ticketId, isAdmin],
  );

  // Configurar presença e typing
  useEffect(() => {
    if (!ticketId) {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }
      setIsOnline(false);
      return;
    }

    const setupPresence = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const channel = supabase
        .channel(`support_presence:${ticketId}`, {
          config: {
            presence: {
              key: user.id,
            },
          },
        })
        // Presence events
        .on("presence", { event: "sync" }, () => {
          const state = channel.presenceState();
          const users = new Map<string, PresenceUser>();

          Object.entries(state).forEach(([userId, presences]) => {
            const presence = Array.isArray(presences)
 
 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
              ? (presences[0] as any)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
              : (presences as any);
            if (presence) {
              users.set(userId, {
                userId,
                status: presence.status || "online",
                lastSeen: presence.lastSeen,
                isAdmin: presence.isAdmin || false,
              });
            }
          });

          setOnlineUsers(users);
        })
 
        .on("presence", { event: "join" }, ({ key, newPresences }) => {
 
          const presence = Array.isArray(newPresences)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
            ? (newPresences[0] as any)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
            : (newPresences as any);
          if (presence) {
            setOnlineUsers((prev) => {
              const next = new Map(prev);
              next.set(key, {
                userId: key,
                status: presence.status || "online",
                lastSeen: presence.lastSeen,
                isAdmin: presence.isAdmin || false,
              });
              return next;
            });
          }
        })
        .on("presence", { event: "leave" }, ({ key }) => {
          setOnlineUsers((prev) => {
            const next = new Map(prev);
            next.delete(key);
            return next;
          });
        })
        // Typing events
        .on("broadcast", { event: "typing" }, ({ payload }) => {
          if (payload.userId !== user.id) {
            setTypingUsers((prev) => new Set(prev).add(payload.userId));

            // Remover typing após 3 segundos
            setTimeout(() => {
              setTypingUsers((prev) => {
                const next = new Set(prev);
                next.delete(payload.userId);
                return next;
              });
            }, 3000);
          }
        })
        .subscribe(async (status) => {
          if (status === "SUBSCRIBED") {
            setIsOnline(true);
            // Enviar presença inicial
            await channel.track({
              userId: user.id,
              status: "online",
              isAdmin,
              lastSeen: new Date().toISOString(),
            });
          }
        });

      channelRef.current = channel;

      // Atualizar presença periodicamente (a cada 30 segundos)
      presenceTimeoutRef.current = setInterval(() => {
        updatePresence("online");
      }, 30000);

      // Detectar quando usuário sai da página
      const handleVisibilityChange = () => {
        if (document.hidden) {
          updatePresence("away");
        } else {
          updatePresence("online");
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        if (presenceTimeoutRef.current) {
          clearInterval(presenceTimeoutRef.current);
        }
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
        channel.unsubscribe();
        channelRef.current = null;
        setIsOnline(false);
      };
    };

    setupPresence();
  }, [ticketId, isAdmin, updatePresence]);

  // Limpar timeouts ao desmontar
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (presenceTimeoutRef.current) {
        clearInterval(presenceTimeoutRef.current);
      }
    };
  }, []);

  return {
    onlineUsers: Array.from(onlineUsers.values()),
    isOnline,
    typingUsers: Array.from(typingUsers),
    sendTyping,
    updatePresence,
  };
}
